import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { ChevronDown, MessageSquarePlus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { AssistantThread } from "@/hooks/use-assistant";

interface ThreadSwitcherProps {
  threads: AssistantThread[] | undefined;
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onNewThread: () => void;
  creating: boolean;
}

export function threadLabel(
  t: AssistantThread,
  tFn: (key: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  if (t.title) return t.title;
  if (t.preview) return t.preview;
  return tFn("thread.newConversationLabel", {
    date: formatDate(new Date(t.createdAt), locale, { dateStyle: "medium" }),
  });
}

export function ThreadSwitcher({
  threads,
  activeThreadId,
  onSelect,
  onNewThread,
  creating,
}: ThreadSwitcherProps) {
  const { t } = useTranslation("assistant");
  const locale = useLocale();
  const active = threads?.find((th) => th.id === activeThreadId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors max-w-[200px]">
        <span className="truncate">
          {active ? threadLabel(active, t, locale) : t("thread.newConversation")}
        </span>
        <ChevronDown size={12} className="flex-shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem disabled={creating} onSelect={() => onNewThread()}>
          <MessageSquarePlus size={14} />
          {t("thread.newThread")}
        </DropdownMenuItem>
        {!!threads?.length && <DropdownMenuSeparator />}
        {threads?.map((th) => (
          <DropdownMenuItem key={th.id} onSelect={() => onSelect(th.id)}>
            <span className="truncate">{threadLabel(th, t, locale)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
